import axios from "axios";
import { RequestQueryBuilder } from "@nestjsx/crud-request";

import router from "@/router";
import { getItem, clear } from "./session";

import apiConfig from "../../api.config";
const serverUrl = apiConfig;

/**
 * Extracts error message from backend response
 * Handles both string messages and NestJS validation error arrays
 */
function extractErrorMessage(data: any, fallback: string): string {
	if (!data || typeof data !== 'object') {
		return fallback;
	}

	const message = data.message;

	// If message is a string, return it
	if (typeof message === 'string') {
		return message;
	}

	// If message is an array (NestJS validation errors)
	if (Array.isArray(message) && message.length > 0) {
		// Extract constraint messages from validation errors
		const validationErrors = message
			.map((err: any) => {
				if (err.constraints) {
					return Object.values(err.constraints).join(', ');
				}
				return null;
			})
			.filter((msg: any) => msg !== null);

		if (validationErrors.length > 0) {
			return validationErrors.join('; ');
		}
	}

	return fallback;
}

const requestErrorHandler = async (error: any) => {
	console.error(error);

	// Tratamento de erros que não são do Axios (sem .response)
	if (!error || typeof error !== "object") {
		throw new Error("API_ERROR");
	}

	// Tratamento de erros sem .response (network error, etc)
	if (!error.response) {
		// Garante que o erro tem .message
		const message = error.message || "API_NETWORK_ERROR";
		throw new Error(message);
	}

	switch (error.response.status) {
		case 401:
			clear();
			router.push("/login");
			return;
		case 403:
			const forbiddenMessage = extractErrorMessage(error.response.data, "API_FORBIDDEN");
			throw new Error(forbiddenMessage);
		case 404:
			throw new Error("API_NOT_FOUND");
		case 429:
			throw new Error("API_RATE_LIMIT");
		case 500:
			throw new Error("API_SERVER_ERROR");
		default:
			const message = extractErrorMessage(error.response.data, "API_ERROR");
			throw new Error(message);
	}
};

function deflateEntity(entity: any, prefix = "") {
	let flatEntity = Object.assign({}, entity);
	Object.keys(entity).forEach(key => {
		if (flatEntity[key] instanceof Array) {
			flatEntity[key] = flatEntity[key].map((subEntity: any) => deflateEntity(subEntity));
		}
		if (flatEntity[key] instanceof Object && !(flatEntity[key] instanceof Array)) {
			flatEntity = { ...flatEntity, ...deflateEntity(flatEntity[key], prefix ? `${prefix}.${key}` : key) };
			delete flatEntity[key];
		} else if (prefix) {
			flatEntity[`${prefix}.${key}`] = flatEntity[key];
			delete flatEntity[key];
		}
	});
	return flatEntity;
}

function inflateEntity(flatEntity: any) {
	const entity = {};
	Object.keys(flatEntity).forEach(key => {
		let entityLevel: any = entity;
		const levelsKeys = key.split(".");
		levelsKeys.forEach((levelKey, level) => {
			if (level === levelsKeys.length - 1) {
				// if last level, set value
				entityLevel[levelKey] = flatEntity[key] instanceof Array ? flatEntity[key].map((subEntity: any) => inflateEntity(subEntity)) : flatEntity[key];
			} else if (!entityLevel[levelKey]) {
				// otherwise, initialize if necessary
				entityLevel[levelKey] = {};
			}
			entityLevel = entityLevel[levelKey]; // update current level
		});
	});
	return entity;
}

export async function post(controller: string, command: string, data: any, sendToken = false) {
	try {
		const response = await axios.request({
			url: `${serverUrl}/${controller}/${command}`,
			method: "post",
			headers: { Authorization: sendToken ? "Bearer " + getItem("token") : "" },
			data,
		});
		if (response.data) {
			return response.data;
		}
		throw new Error("API_DATA_NOT_FOUND");
	} catch (error) {
		return requestErrorHandler(error);
	}
}

export async function crudDescription(controller: string) {
	try {
		const response = await axios.get(`${serverUrl}/${controller}/getEntityDescription`, {
			headers: { Authorization: "Bearer " + getItem("token") },
		});
		return response.data;
	} catch (error) {
		return requestErrorHandler(error);
	}
}

export async function crudCreate(controller: string, data: any) {
	try {
		const response = await axios.request({
			url: `${serverUrl}/${controller}`,
			method: "post",
			headers: { Authorization: "Bearer " + getItem("token") },
			data: inflateEntity(data),
		});
		if (response.data) {
			return response.data;
		}
		throw new Error("API_DATA_NOT_FOUND");
	} catch (error) {
		return requestErrorHandler(error);
	}
}

export async function crudQuery(controller: string, queryParameters: any) {
	const queryString = RequestQueryBuilder.create(queryParameters)
		.query()
		.replace(/%5B\d+%5D/gi, "%5B%5D"); // remove array indices from brackets due to array limitation of 21 items
	try {
		const response = await axios.get(`${serverUrl}/${controller}?${queryString}`, {
			headers: { Authorization: "Bearer " + getItem("token") },
		});
		if (response.data && Array.isArray(response.data)) {
			response.data = response.data.map(entity => deflateEntity(entity));
		} else if (response.data && response.data.data && Array.isArray(response.data.data)) {
			response.data.data = response.data.data.map((entity: any) => deflateEntity(entity));
		}
		return response.data;
	} catch (error) {
		return requestErrorHandler(error);
	}
}

export async function crudRead(controller: string, id: string | number, queryParameters: any) {
	const queryString = RequestQueryBuilder.create(queryParameters)
		.query()
		.replace(/%5B\d+%5D/gi, "%5B%5D");
	try {
		const response = await axios.get(`${serverUrl}/${controller}/${id}?${queryString}`, {
			headers: { Authorization: "Bearer " + getItem("token") },
		});
		return response.data ? deflateEntity(response.data) : null;
	} catch (error) {
		return requestErrorHandler(error);
	}
}

export async function crudUpdate(controller: string, id: string | number, data: any) {
	try {
		const response = await axios.request({
			url: `${serverUrl}/${controller}/${id}`,
			method: "patch",
			headers: { Authorization: "Bearer " + getItem("token") },
			data: inflateEntity(data),
		});
		if (response.data) {
			return response.data;
		}
		throw new Error("API_DATA_NOT_FOUND");
	} catch (error) {
		return requestErrorHandler(error);
	}
}

export async function crudDelete(controller: string, id: string | number, auditNotes: string) {
	try {
		await axios.request({
			url: `${serverUrl}/${controller}/${id}`,
			method: "delete",
			headers: { Authorization: "Bearer " + getItem("token") },
			data: { auditNotes },
		});
		return true;
	} catch (error) {
		return requestErrorHandler(error);
	}
}

export async function crudPost(controller: string, path: string, data?: any, blob = false) {
	try {
		const response = await axios.post(`${serverUrl}/${controller}/${path}`, data, {
			headers: { Authorization: "Bearer " + getItem("token") },
			responseType: blob ? "blob" : "json",
		});
		return response.data;
	} catch (error) {
		return requestErrorHandler(error);
	}
}

export async function crudGet(controller: string, path: string, params?: any) {
	try {
		const response = await axios.get(`${serverUrl}/${controller}/${path}`, {
			headers: { Authorization: "Bearer " + getItem("token") },
			params,
		});
		return response.data;
	} catch (error) {
		return requestErrorHandler(error);
	}
}

export async function uploadFile(file: any) {
	const formData = new FormData();
	formData.append("file", file);
	try {
		const response = await axios.request({
			url: "https://upload.sistemagerenciador.app.br/",
			method: "post",
			data: formData,
		});
		if (response.data) {
			return response.data;
		}
		throw new Error("API_DATA_NOT_FOUND");
	} catch (error) {
		return requestErrorHandler(error);
	}
}
